import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, FileText, Shield, Eye, Edit2, HelpCircle, Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { API, getToken } from './shared';

const pages = [
  { slug: 'about-us', label: 'About Us', icon: Users },
  { slug: 'terms-and-conditions', label: 'Terms & Conditions', icon: FileText },
  { slug: 'privacy-policy', label: 'Privacy Policy', icon: Shield }
];

const PagesTab = ({ siteContent, setSiteContent }) => {
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState('');
  const [savingPage, setSavingPage] = useState(false);

  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faqs`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setFaqs(res.data);
    } catch (error) { console.error('Error fetching FAQs:', error); }
  };

  const handleSavePage = async () => {
    setSavingPage(true);
    try {
      await axios.put(`${API}/admin/site-content/${editingPage.slug}`, { content: pageContent }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSiteContent({ ...siteContent, [editingPage.slug]: pageContent });
      setEditingPage(null);
    } catch (error) {
      console.error('Error saving page:', error);
      alert('Failed to save page content.');
    } finally { setSavingPage(false); }
  };

  const handleSaveFaq = async () => {
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faqs/${editingFaq.id}`, { question: faqForm.question, answer: faqForm.answer }, { headers: { Authorization: `Bearer ${getToken()}` } });
      } else {
        await axios.post(`${API}/admin/faqs`, { question: faqForm.question, answer: faqForm.answer, order: faqs.length }, { headers: { Authorization: `Bearer ${getToken()}` } });
      }
      setShowFaqForm(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '' });
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Failed to save FAQ.');
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      fetchFaqs();
    } catch (error) { console.error('Error deleting FAQ:', error); }
  };

  const handleMoveFaq = async (index, direction) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= faqs.length) return;
    try {
      const config = { headers: { Authorization: `Bearer ${getToken()}` } };
      await Promise.all([
        axios.put(`${API}/admin/faqs/${faqs[index].id}`, { order: faqs[swapIdx].order }, config),
        axios.put(`${API}/admin/faqs/${faqs[swapIdx].id}`, { order: faqs[index].order }, config)
      ]);
      fetchFaqs();
    } catch (error) { console.error('Error reordering FAQ:', error); }
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-semibold mb-6">Manage Pages</h2>
      <div className="space-y-4 mb-10" data-testid="pages-list">
        {pages.map(page => (
          <div key={page.slug} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`page-card-${page.slug}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <page.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">{page.label}</h3>
                  <p className="text-sm text-slate-500">
                    {siteContent[page.slug] ? `${siteContent[page.slug].length} characters · Custom content` : 'Using default content'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer" className="h-9 px-4 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" /> Preview
                </a>
                <button onClick={() => { setEditingPage(page); setPageContent(siteContent[page.slug] || ''); }} className="h-9 px-4 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all flex items-center gap-1.5" data-testid={`edit-page-${page.slug}`}>
                  <Edit2 className="w-3.5 h-3.5" /> Edit Content
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FAQ Management */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold">FAQs</h2>
          <p className="text-sm text-slate-500 mt-1">Manage frequently asked questions shown on the homepage</p>
        </div>
        <button
          onClick={() => { setEditingFaq(null); setFaqForm({ question: '', answer: '' }); setShowFaqForm(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
          data-testid="add-faq-btn"
        >
          <Plus className="w-5 h-5" /> Add FAQ
        </button>
      </div>

      {faqs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No FAQs Yet</h3>
          <p className="text-slate-500">Add your first FAQ to display on the homepage</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="faq-list">
          {faqs.map((faq, idx) => (
            <div key={faq.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`faq-item-${faq.id}`}>
              <div className="flex gap-3">
                <div className="flex flex-col items-center gap-1 pt-1">
                  <button onClick={() => handleMoveFaq(idx, -1)} disabled={idx === 0} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-all disabled:opacity-30" data-testid={`faq-move-up-${faq.id}`}>
                    <ChevronUp className="w-4 h-4 text-slate-600" />
                  </button>
                  <span className="text-xs text-slate-400 font-medium">{idx + 1}</span>
                  <button onClick={() => handleMoveFaq(idx, 1)} disabled={idx === faqs.length - 1} className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-all disabled:opacity-30" data-testid={`faq-move-down-${faq.id}`}>
                    <ChevronDown className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-900 mb-1">{faq.question}</h4>
                  <p className="text-sm text-slate-500 line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => { setEditingFaq(faq); setFaqForm({ question: faq.question, answer: faq.answer }); setShowFaqForm(true); }} className="h-9 px-4 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all flex items-center gap-1.5" data-testid={`faq-edit-${faq.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDeleteFaq(faq.id)} className="h-9 px-4 rounded-lg bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-all flex items-center gap-1.5" data-testid={`faq-delete-${faq.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAQ Form Modal */}
      {showFaqForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowFaqForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()} data-testid="faq-form-modal">
            <h3 className="text-2xl font-bold mb-6">{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
                <input type="text" value={faqForm.question} onChange={e => setFaqForm({ ...faqForm, question: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="e.g., How do I list my property?" data-testid="faq-input-question" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Answer</label>
                <textarea value={faqForm.answer} onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })} rows={5} className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-y" placeholder="Write a clear, helpful answer..." data-testid="faq-input-answer" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={handleSaveFaq} disabled={!faqForm.question || !faqForm.answer} className="flex-1 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="faq-save-btn">
                  {editingFaq ? 'Update FAQ' : 'Add FAQ'}
                </button>
                <button onClick={() => setShowFaqForm(false)} className="h-11 px-6 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Editor Modal */}
      {editingPage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingPage(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()} data-testid="page-editor-modal">
            <h3 className="text-2xl font-bold mb-2">Edit {editingPage.label}</h3>
            <p className="text-sm text-slate-500 mb-6">
              Edit the content below. Use numbered lines like "1. Title" for section headings. Each paragraph should be on a new line. Leave empty to use the default built-in content.
            </p>
            <textarea value={pageContent} onChange={e => setPageContent(e.target.value)} rows={20} className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-y font-mono leading-relaxed" placeholder="Enter page content... Leave empty to use default." data-testid="page-content-editor" />
            <div className="flex gap-3 pt-4">
              <button onClick={handleSavePage} disabled={savingPage} className="flex-1 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50" data-testid="save-page-btn">
                {savingPage ? 'Saving...' : 'Save Content'}
              </button>
              <button onClick={() => setEditingPage(null)} className="h-11 px-6 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagesTab;
